/** Multiple-choice step — single or multi-select with keyboard shortcuts and grid layout. */
import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Check, X } from 'lucide-react';
import { useSession } from '../SessionContext';
import { AnimatedKbd, useShortcutOverlay } from '../components/ShortcutOverlay';
import { useKeyboardRipple, RippleFlash } from '../components/KeyboardRipple';
import { StepShell } from './StepShell';
import {
  PromptRenderer,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { FeedbackDisplay } from './FeedbackDisplay';
import type { McqConfig } from '../types';

interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

const EMPTY_OPTIONS: McqConfig['options'] = [];
const EMPTY_IDS: string[] = [];

/**
 * Multiple-choice step. Supports single and multi-select modes.
 */
export function MCQStep() {
  const { currentStep, state, dispatch } = useSession();
  const { showShortcuts } = useShortcutOverlay();
  const { rippleIndex, rippleKey } = useKeyboardRipple();

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = (snapshot?.config ?? null) as unknown as McqConfig | null;
  const options = config?.options ?? EMPTY_OPTIONS;
  const allowMultiple = config?.allowMultiple ?? false;

  const answer = state.answer?.type === 'mcq' ? state.answer : null;
  const selectedIds = answer?.selectedIds ?? EMPTY_IDS;
  const phase = state.phase;
  const result = state.result;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';
  const correctIds = result?.correctChoiceIds ?? EMPTY_IDS;
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Determine if grid layout is appropriate: 2-4 options all under 40 chars
  const useGrid = useMemo(() => {
    return options.length >= 2 && options.length <= 4 && options.every((o) => o.text.length < 40);
  }, [options]);

  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);

  const diagramJson = (currentStep as unknown as Record<string, unknown>)?.diagramJson as
    | DiagramJson
    | null
    | undefined;

  if (!currentStep || !snapshot) return null;

  function handleSelect(optionId: string) {
    if (isDisabled || isGraded) return;

    let next: string[];
    if (allowMultiple) {
      next = selectedIds.includes(optionId)
        ? selectedIds.filter((id) => id !== optionId)
        : [...selectedIds, optionId];
    } else {
      next = [optionId];
    }
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'mcq', selectedIds: next } });
  }

  function getOptionStyle(optionId: string): React.CSSProperties {
    const isSelected = selectedIds.includes(optionId);
    const isCorrect = correctIds.includes(optionId);

    if (isGraded) {
      if (isSelected && isCorrect) {
        return {
          borderColor: 'var(--f-success)',
          backgroundColor: 'var(--f-success-subtle)',
          color: 'var(--f-success-text)',
        };
      }
      if (isSelected && !isCorrect) {
        return {
          borderColor: 'var(--f-error)',
          backgroundColor: 'var(--f-error-subtle)',
          color: 'var(--f-error-text)',
        };
      }
      if (!isSelected && isCorrect) {
        return {
          borderColor: 'var(--f-success)',
          backgroundColor: 'var(--f-success-subtle)',
          color: 'var(--f-success-text)',
        };
      }
      return {
        borderColor: 'var(--f-border)',
        backgroundColor: 'var(--f-bg-raised)',
        color: 'var(--f-fg-muted)',
      };
    }

    if (isSelected) {
      return {
        borderColor: 'var(--f-accent)',
        backgroundColor: 'var(--f-accent-subtle)',
        color: 'var(--f-fg)',
      };
    }

    return {
      borderColor: hoveredId === optionId ? 'var(--f-accent)' : 'var(--f-border)',
      backgroundColor: 'var(--f-bg-raised)',
      color: 'var(--f-fg)',
    };
  }

  function getIcon(optionId: string) {
    if (!isGraded) return null;
    const isSelected = selectedIds.includes(optionId);
    const isCorrect = correctIds.includes(optionId);

    if ((isSelected && isCorrect) || (!isSelected && isCorrect)) {
      return (
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 25 }}
        >
          <Check size={18} />
        </motion.span>
      );
    }
    if (isSelected && !isCorrect) {
      return (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <X size={18} />
        </motion.span>
      );
    }
    return null;
  }

  return (
    <StepShell>
      <PromptRenderer
        doc={snapshot.prompt as TiptapNode}
        media={mediaItems}
        diagramJson={diagramJson ?? null}
      />

      <div
        className={useGrid ? 'grid grid-cols-2 gap-3' : 'flex flex-col gap-3'}
        style={{
          opacity: isDisabled ? 0.7 : 1,
          pointerEvents: isDisabled ? 'none' : 'auto',
        }}
      >
        {options.map((option, index) => (
          <motion.button
            key={option.id}
            type="button"
            onClick={() => handleSelect(option.id)}
            onMouseEnter={() => !isGraded && !isDisabled && setHoveredId(option.id)}
            onMouseLeave={() => setHoveredId(null)}
            whileHover={
              !isGraded && !isDisabled
                ? { scale: 1.02, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }
                : undefined
            }
            whileTap={!isGraded && !isDisabled ? { scale: 0.98 } : undefined}
            animate={
              isGraded && selectedIds.includes(option.id) && correctIds.includes(option.id)
                ? { scale: [1, 1.02, 1] }
                : undefined
            }
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
            style={{
              ...getOptionStyle(option.id),
              transition: 'border-color 0.2s ease',
              padding: 16,
              borderRadius: 'var(--f-radius-xl)',
              borderWidth: '2px',
              borderStyle: 'solid',
              fontFamily: 'var(--f-font-sans)',
              fontSize: 'var(--f-text-base)',
              lineHeight: 'var(--f-leading-snug)',
              cursor: isGraded ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--f-space-2)',
              textAlign: 'left',
              position: 'relative',
              outline: 'none',
            }}
          >
            <RippleFlash active={rippleIndex === index} rippleKey={rippleKey} />
            <AnimatedKbd show={!isGraded && showShortcuts}>{index + 1}</AnimatedKbd>
            <span>{option.text}</span>
            <AnimatePresence mode="wait">{getIcon(option.id)}</AnimatePresence>
          </motion.button>
        ))}
      </div>

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
